"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, type SVGProps } from "react";
import { customerSupportTelHref, isSiteActive } from "@/shared/config/customerSupport";
import styles from "./ProviderPageContent.corrected.module.scss";

const PROVIDER_REGISTER_HREF = "/for-providers?auth=register&role=provider";
const PROVIDER_ASSET_ROOT = "/assets/providerPage";
const CORRECTED_PROVIDER_ASSET_ROOT = "/assets/corrected/provider";
const LANDING_ASSET_ROOT = "/assets/corrected/landing";
const HERO_ASSET_ROOT = "/assets/corrected/provider";

const providerBenefits = [
  {
    title: ["Purpose Driven", "Work"],
    copy: "Support families during life's most important moments.",
    icon: `${PROVIDER_ASSET_ROOT}/promise-purpose.svg`,
  },
  {
    title: ["Flexible", "Schedule"],
    copy: "Choose assignments that fit your availability.",
    icon: `${PROVIDER_ASSET_ROOT}/promise-schedule.svg`,
  },
  {
    title: ["Work Near", "Home"],
    copy: "Accept cases close to your preferred location.",
    icon: `${PROVIDER_ASSET_ROOT}/promise-work-near-home.svg`,
  },
  {
    title: ["Fair & Transparent", "Earnings"],
    copy: "Competitive and Timely payouts.",
    icon: `${PROVIDER_ASSET_ROOT}/promise-earnings.svg`,
  },
  {
    title: ["Professional", "Support"],
    copy: "Clinical guidance whenever needed.",
    icon: `${PROVIDER_ASSET_ROOT}/promise-support.svg`,
  },
  {
    title: ["Career", "Growth"],
    copy: "Training and opportunities to specialize.",
    icon: `${PROVIDER_ASSET_ROOT}/promise-growth.svg`,
  },
] as const;

const providerComparison = {
  traditional: [
    "Limited visibility",
    "Manual paperwork",
    "Minimal support",
    "Fixed schedules",
    "Delayed communication",
    "Limited recognition",
  ],
  carevinco: [
    "Transparent assignments",
    "Digital workflow",
    "Dedicated coordinators",
    "Flexible working hours",
    "Real-time support",
    "Career growth",
  ],
} as const;

const comparisonRail = [
  { label: "Visibility", icon: `${PROVIDER_ASSET_ROOT}/compare-eye.svg` },
  { label: "Digital documentation", icon: `${PROVIDER_ASSET_ROOT}/compare-document.svg` },
  { label: "Support", icon: `${PROVIDER_ASSET_ROOT}/compare-support.svg` },
  { label: "Flexible hours", icon: `${PROVIDER_ASSET_ROOT}/compare-calendar.svg` },
  { label: "Communication", icon: `${PROVIDER_ASSET_ROOT}/compare-message.svg` },
  { label: "Recognition", icon: `${PROVIDER_ASSET_ROOT}/compare-star.svg` },
] as const;

const providerSteps = [
  {
    title: "Submit Your Application",
    copy: "Complete our online application and upload your credentials.",
  },
  {
    title: "Verification & Screening",
    copy: "Our team verifies your qualifications, nursing registration, identity and experience.",
  },
  {
    title: "Orientation & Onboarding",
    copy: "Complete onboarding to master Carevinco's standards and platform.",
  },
  {
    title: "Start Receiving Assignments",
    copy: "Accept nursing assignments that match your skills, availability and preferred service area.",
  },
] as const;

const providerTracks = [
  {
    title: "Registered Nurses",
    copy: "For qualified nurses ready to deliver compassionate care at home.",
    items: ["B.Sc Nursing", "GNM", "ANM"],
    icon: `${PROVIDER_ASSET_ROOT}/track-registered-nurse.webp`,
  },
  {
    title: "Specialized Nurses",
    copy: "For nurses with advanced skills and specialized clinical experience.",
    items: ["ICU", "Critical Care", "Dialysis", "NICU", "Pediatric"],
    icon: `${PROVIDER_ASSET_ROOT}/track-specialized-nurse.webp`,
  },
  {
    title: "Home Visit Specialists",
    copy: "For caregivers who provide essential support in home settings.",
    items: ["Post Surgical Care", "Wound Care", "Geriatric Care", "Palliative Care", "Chronic Disease Management"],
    icon: `${PROVIDER_ASSET_ROOT}/track-home-visit-specialist.webp`,
  },
] as const;

const providerWorkflow = [
  { lines: ["Receive", "Assignment"], image: `${PROVIDER_ASSET_ROOT}/workflow-assignment.webp` },
  { lines: ["Review", "Patient"], image: `${PROVIDER_ASSET_ROOT}/workflow-patient.webp` },
  { lines: ["Visit", "Home"], image: `${PROVIDER_ASSET_ROOT}/workflow-visit.webp` },
  { lines: ["Deliver", "Care"], image: `${PROVIDER_ASSET_ROOT}/workflow-care.webp` },
  { lines: ["Digital", "Documentation"], image: `${PROVIDER_ASSET_ROOT}/workflow-documentation.webp` },
  { lines: ["Family", "Updates"], image: `${PROVIDER_ASSET_ROOT}/workflow-family.webp` },
  { lines: ["Receive", "Payment"], image: `${PROVIDER_ASSET_ROOT}/workflow-payment.webp` },
] as const;

const providerCommitments = [
  { lines: ["Fair and transparent", "compensation"], icon: `${PROVIDER_ASSET_ROOT}/support-compensation.webp` },
  { lines: ["Clear clinical", "protocols"], icon: `${PROVIDER_ASSET_ROOT}/support-protocol.webp` },
  { lines: ["Prompt payment", "processing"], icon: `${PROVIDER_ASSET_ROOT}/support-payment.webp` },
  { lines: ["Professional", "development"], icon: `${PROVIDER_ASSET_ROOT}/support-development.webp` },
  { lines: ["Dedicated care", "coordination"], icon: `${PROVIDER_ASSET_ROOT}/support-coordination.webp` },
  { lines: ["Recognition for", "outstanding service"], icon: `${PROVIDER_ASSET_ROOT}/support-recognition.webp` },
] as const;

const providerFaqs = [
  {
    question: "What is Carevinco?",
    answer: "Carevinco is a care management platform that helps families connect with verified caregivers, nurses, doctors, and wellness professionals while staying informed throughout the care journey.",
  },
  {
    question: "How do I request a care service?",
    answer: "Simply choose a service, tell us what you need, and our care team matches you with a verified nurse—usually within hours.",
  },
  {
    question: "What services does Carevinco provide?",
    answer: "We offer home nursing, elder care, post-hospital care, palliative care, and personalized wellness support delivered at home.",
  },
  {
    question: "Are all care providers verified?",
    answer: "Yes, every provider is identity-verified, background-checked, and vetted for qualifications before joining Carevinco.",
  },
  {
    question: "Can I choose my preferred nurse?",
    answer: "Absolutely. You can choose a nurse based on your preferred gender and language, so you can select someone who best fits your family's needs.",
  },
  {
    question: "How will I know what happens during a visit?",
    answer: "You'll receive real-time visit updates and care reports directly on the app after every visit.",
  },
  {
    question: "Can I book care services for my parents while living abroad?",
    answer: "Yes, families abroad can book, manage, and track care visits remotely with complete transparency.",
  },
  {
    question: "What happens if I am not satisfied with a provider?",
    answer: "Our care coordination team will help you switch providers or resolve any concerns promptly.",
  },
  {
    question: "Are the care plans personalized?",
    answer: "Every care plan is tailored to the individual's health needs, recovery goals, and daily routine.",
  },
  {
    question: "Is emergency support available?",
    answer: "Carevinco can help with care coordination and guidance, but it is not an emergency service. For urgent medical help, contact your local emergency services immediately.",
  },
  {
    question: "How do payments work?",
    answer: "Pricing is transparent and shown upfront—you only pay for the care you actually receive.",
  },
  {
    question: "Can I track appointments and schedules?",
    answer: "Yes, all appointments and schedules can be tracked in real time through the Carevinco app.",
  },
  {
    question: "Will I receive regular updates about my loved one?",
    answer: "Yes, you'll get regular updates and visit summaries so you always know how your loved one is doing.",
  },
  {
    question: "Is my personal information secure?",
    answer: "Your data is encrypted and handled with strict privacy and security standards at every step.",
  },
] as const;

type IconProps = SVGProps<SVGSVGElement>;

function HeartIcon(props: IconProps) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" {...props}>
      <path d="M12 21s-7-4.35-9.33-8.28C.9 9.73 2.7 5.5 6.5 5.5c2.1 0 3.5 1.2 4.5 2.5 1-1.3 2.4-2.5 4.5-2.5 3.8 0 5.6 4.23 3.83 7.22C19 16.65 12 21 12 21Z" />
    </svg>
  );
}

function PhoneIcon(props: IconProps) {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" {...props}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.8 19.8 0 0 1 3.1 5.18 2 2 0 0 1 5.1 3h3a2 2 0 0 1 2 1.72 12.8 12.8 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L9.08 10.9a16 16 0 0 0 4 4l1.26-1.26a2 2 0 0 1 2.11-.45 12.8 12.8 0 0 0 2.81.7A2 2 0 0 1 22 16.92Z" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function CalendarIcon(props: IconProps) {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" {...props}>
      <rect height="18" rx="2" stroke="currentColor" strokeWidth="2" width="18" x="3" y="4" />
      <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function StarIcon(props: IconProps) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" {...props}>
      <path d="m12 2 3.09 6.26 6.91 1-5 4.87 1.18 6.87L12 17.77l-6.18 3.23L7 14.13 2 9.26l6.91-1L12 2Z" />
    </svg>
  );
}

function ChevronIcon({ open, ...props }: IconProps & { open: boolean }) {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" {...props}>
      <path d="M4.8125 15.7108 11.0579 9.4657a1.35 1.35 0 0 1 1.9092 0l6.2454 6.2454" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" />
    </svg>
  );
}

function ComparisonMark({ positive }: { positive: boolean }) {
  return positive ? (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
      <path d="m6.75 12.3 3.2 3.2 7.3-7.3" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.4" />
    </svg>
  ) : (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
      <path d="m8 8 8 8m0-8-8 8" stroke="currentColor" strokeLinecap="round" strokeWidth="2.4" />
    </svg>
  );
}

function ProviderHero() {
  const avatars = [1, 2, 3].map((index) => `${LANDING_ASSET_ROOT}/family-${index}-64.webp`);

  return (
    <section className={styles.hero} aria-labelledby="provider-hero-title">
      <div className={`${styles.heroInner} container`}>
        <div className={styles.heroCopy}>
          <span className={styles.heroBadge}>
            <HeartIcon />
            Competitive and Timely payouts.
          </span>
          <h1 id="provider-hero-title">A Better Way to Build Your Career</h1>
          <p>
            Compassionate care meets smart technology. Transparent pay, real support, and a platform built for nurses to thrive. Combine compassionate healthcare with technology, transparency, and professional support to create an experience that helps nurses thrive. Support families with compassionate home healthcare
          </p>
          <div className={styles.heroActions}>
            {isSiteActive ? (
              <>
              <a href={customerSupportTelHref}>
                <PhoneIcon />
                Talk to Care Expert
              </a>
              <Link href={PROVIDER_REGISTER_HREF}>
                <CalendarIcon />
                Apply Now
              </Link>
            </>
            ) : null}
          </div>
          <div className={styles.heroTrust}>
            <div className={styles.heroAvatars}>
              {avatars.map((avatar, index) => (
                <span className={styles.heroAvatar} key={avatar}>
                  <Image alt={`Carevinco family ${index + 1}`} fill sizes="36px" src={avatar} />
                </span>
              ))}
            </div>
            <span className={styles.heroRating}>
              4.9/5 <StarIcon />
            </span>
            <span className={styles.heroTrusted}>Trusted by 5000 Families</span>
          </div>
        </div>
        <div className={styles.heroImage}>
          <Image
            alt="Home nurse caring for an elderly couple"
            fill
            fetchPriority="high"
            priority
            sizes="(max-width: 639px) calc(100vw - 68px), (max-width: 768px) calc(100vw - 72px), (max-width: 1024px) calc(50vw - 56px), 44vw"
            src={`${HERO_ASSET_ROOT}/hero-provider-optimized.webp`}
          />
        </div>
      </div>
    </section>
  );
}

function ProviderFaq() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section className={styles.faqSection} id="faq" aria-labelledby="provider-faq-title">
      <div className={styles.faqContainer}>
        <header className={styles.faqHeading}>
          <p>Frequently Asked Questions</p>
          <h2 id="provider-faq-title">Carevinco Premium FAQs</h2>
        </header>
        <div className={styles.faqList}>
          {providerFaqs.map((faq, index) => {
            const open = openIndex === index;
            const buttonId = `provider-faq-button-${index}`;
            const panelId = `provider-faq-panel-${index}`;
            return (
              <article className={styles.faqCard} key={faq.question}>
                <button
                  aria-controls={panelId}
                  aria-expanded={open}
                  className={styles.faqQuestion}
                  id={buttonId}
                  onClick={() => setOpenIndex(open ? -1 : index)}
                  type="button"
                >
                  <span>{faq.question}</span>
                  <ChevronIcon className={`${styles.faqChevron}${open ? ` ${styles.faqChevronOpen}` : ""}`} open={open} />
                </button>
                <p aria-labelledby={buttonId} className={styles.faqAnswer} hidden={!open} id={panelId} role="region">
                  {faq.answer}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function ProviderPageContent() {
  return (
    <main className={styles.page} id="provider-page">
      <ProviderHero />

      <section aria-labelledby="provider-promise-title" className={`${styles.section} ${styles.promiseSection}`} id="why">
        <div className={`${styles.container} ${styles.promisePanel}`}>
          <header className={styles.sectionHeader}>
            <p className={styles.kicker}>Why Nurses Choose Carevinco</p>
            <h2 className={styles.title} id="provider-promise-title">
              <span>You&apos;re More Than a</span> Healthcare Professional
            </h2>
            <p className={styles.copy}>
              At Carevinco, we believe every nurse deserves respect, flexibility, and opportunities to grow while making a meaningful difference in people&apos;s lives.
            </p>
          </header>

          <div className={styles.promiseGrid}>
            {providerBenefits.map((benefit) => (
              <article className={styles.promiseCard} key={benefit.title.join(" ")}>
                <span aria-hidden="true" className={styles.promiseIcon}>
                  <Image alt="" height={60} src={benefit.icon} width={60} />
                </span>
                <h3>
                  {benefit.title[0]}
                  <br />
                  {benefit.title[1]}
                </h3>
                <p>{benefit.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section aria-labelledby="provider-comparison-title" className={`${styles.section} ${styles.sectionSoft}`}>
        <div className={styles.container}>
          <header className={styles.sectionHeader}>
            <p className={styles.kicker}>Built Around Nurses, Not Just Assignments</p>
            <h2 className={styles.title} id="provider-comparison-title">
              A <span>Better Way</span> to Build Your Career
            </h2>
            <p className={styles.copy}>
              We combine compassionate healthcare with technology, transparency, and professional support to create an experience that helps nurses thrive.
            </p>
          </header>

          <div className={styles.comparison}>
            <article className={styles.compareCard}>
              <h3>Traditional Agency</h3>
              <ul>
                {providerComparison.traditional.map((item) => (
                  <li key={item}>
                    <span aria-hidden="true" className={`${styles.compareMark} ${styles.compareMarkNegative}`}>
                      <ComparisonMark positive={false} />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </article>

            <div aria-label="Carevinco workflow" className={styles.comparisonRail}>
              {comparisonRail.map((item) => (
                <span key={item.label}>
                  <Image alt={item.label} height={25} src={item.icon} width={25} />
                </span>
              ))}
            </div>

            <article className={styles.compareCard}>
              <h3>Carevinco</h3>
              <ul>
                {providerComparison.carevinco.map((item) => (
                  <li key={item}>
                    <span aria-hidden="true" className={`${styles.compareMark} ${styles.compareMarkPositive}`}>
                      <ComparisonMark positive />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </article>
          </div>
        </div>
      </section>

      <section aria-label="Provider application" className={styles.section} id="apply">
        <div className={`${styles.container} ${styles.applicationGrid} ${styles.navAligned} container`}>
          <article className={styles.applicationPanel}>
            <p className={styles.kicker}>Getting Started</p>
            <h2 className={styles.panelTitle}>
              Join in 4 <span>Simple Steps</span>
            </h2>
            <ol className={styles.stepList}>
              {providerSteps.map((step, index) => (
                <li className={styles.step} key={step.title}>
                  <span aria-hidden="true" className={styles.stepNumber}>{index + 1}</span>
                  <div className={styles.stepCard}>
                    <h3>{step.title}</h3>
                    <p>{step.copy}</p>
                  </div>
                </li>
              ))}
            </ol>
          </article>
        </div>
      </section>

      <section aria-labelledby="provider-tracks-title" className={`${styles.section} ${styles.tracksSection}`}>
        <div aria-hidden="true" className={styles.tracksBackdrop}>
          <Image alt="" fill sizes="100vw" src={`${CORRECTED_PROVIDER_ASSET_ROOT}/professional-tracks-background.png`} />
        </div>
        <div className={`${styles.container} ${styles.navAligned} container`}>
          <header className={`${styles.sectionHeader} ${styles.sectionHeaderLeft} ${styles.tracksHeader}`}>
            <p className={styles.kicker}>Qualified Care Professionals</p>
            <h2 className={styles.title} id="provider-tracks-title">
              Experienced Professionals. <span>Compassionate</span> People.
            </h2>
            <p className={styles.copy}>
              Every Carevinco professional is carefully selected, verified, and trained to deliver safe, personalized, and compassionate nursing care.
            </p>
          </header>

          <div className={styles.tracksGrid}>
            {providerTracks.map((track) => (
              <article className={styles.trackCard} key={track.title}>
                <header className={styles.trackHeading}>
                  <Image alt="" height={64} src={track.icon} width={64} />
                  <div>
                    <h3>{track.title}</h3>
                    <p>{track.copy}</p>
                  </div>
                </header>
                <ul>
                  {track.items.map((item) => <li key={item}>{item}</li>)}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section aria-labelledby="provider-workflow-title" className={`${styles.section} ${styles.workflowSection}`}>
        <div className={styles.container}>
          <header className={styles.sectionHeader}>
            <h2 className={styles.title} id="provider-workflow-title">
              <span>Simple. Connected. Rewarding.</span>
              <br />
              Your Day, Designed Around Care.
            </h2>
          </header>

          <div className={styles.workflowGrid}>
            {providerWorkflow.map((step) => (
              <article className={styles.workflowCard} key={step.lines.join(" ")}>
                <span className={styles.workflowImage}>
                  <Image alt={step.lines.join(" ")} height={120} src={step.image} width={120} />
                </span>
                <strong>
                  {step.lines[0]}
                  <br />
                  {step.lines[1]}
                </strong>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section aria-labelledby="provider-commitment-title" className={`${styles.section} ${styles.commitmentSection}`}>
        <div className={`${styles.container} ${styles.commitmentPanel} container`}>
          <div aria-hidden="true" className={styles.commitmentBackdrop}>
            <Image alt="" fill sizes="(max-width: 768px) 0px, 70vw" src={`${CORRECTED_PROVIDER_ASSET_ROOT}/commitment-background.webp`} />
          </div>
          <div className={styles.commitmentContent}>
            <p className={styles.kicker}>Our Commitment to you</p>
            <h2 className={styles.title} id="provider-commitment-title">
              We&apos;re Committed <span>To Your Success</span>
            </h2>
            <p className={`${styles.copy} ${styles.commitmentCopy}`}>At Carevinco, we believe in supporting our nursing partners with:</p>
            <div className={styles.commitmentGrid}>
              {providerCommitments.map((commitment) => (
                <article className={styles.commitmentCard} key={commitment.lines.join(" ")}>
                  <Image alt="" height={50} src={commitment.icon} width={50} />
                  <h3>
                    {commitment.lines[0]}
                    <br />
                    {commitment.lines[1]}
                  </h3>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <ProviderFaq />
    </main>
  );
}
